import React, { useState, useContext, useEffect } from 'react';
import {
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Stack,
  LinearProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  PhotoCamera as CameraIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Language as WebIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import AuthContext from '../../context/AuthContext';
import { getSocialDisplayLink } from '../../utils/socialHelpers';
import { getProfileImageUrl } from '../../utils/urlHelpers';
import { validateProfileImage } from '../../utils/profileImageUpload';

const ProfilePage = () => {
  const { user, updateProfile, loading, error } = useContext(AuthContext);
  
  const [tabValue, setTabValue] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [success, setSuccess] = useState(false);
  const [localError, setLocalError] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [genre, setGenre] = useState('');
  const [genres, setGenres] = useState([]);
  
  // Upload progress tracking
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    artistName: '',
    bio: '',
    instagram: '',
    facebook: '',
    twitter: '',
    website: ''
  });
  
  useEffect(() => {
    if (user) {
      // Initialize form data from user
      setFormData({
        username: user.username || '',
        email: user.email || '',
        artistName: user.artistName || '',
        bio: user.bio || '',
        instagram: user.socialLinks?.instagram || '',
        facebook: user.socialLinks?.facebook || '',
        twitter: user.socialLinks?.twitter || '',
        website: user.socialLinks?.website || ''
      });
      
      // Initialize genres
      if (user.genre && Array.isArray(user.genre)) {
        setGenres(user.genre);
      }
    }
  }, [user]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear validation errors when user starts typing
    if (localError && name === 'artistName' && value.trim()) {
      setLocalError('');
    }
  };
  
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate the image
      const validation = validateProfileImage(file);
      if (!validation.isValid) {
        setLocalError(validation.errors.join(', '));
        return;
      }
      
      // Clear any previous errors
      setLocalError('');
      
      setProfileImage(file);
      setProfileImagePreview(URL.createObjectURL(file));
    }
  };
  
  const handleAddGenre = () => {
    if (genre.trim() !== '' && !genres.includes(genre.trim())) {
      setGenres([...genres, genre.trim()]);
      setGenre('');
    }
  };
  
  const handleDeleteGenre = (genreToDelete) => {
    setGenres(genres.filter(g => g !== genreToDelete));
  };
  
  const handleGenreKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddGenre();
    }
  };
  
  const handleCancel = () => {
    // Reset form data to user data
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        artistName: user.artistName || '',
        bio: user.bio || '',
        instagram: user.socialLinks?.instagram || '',
        facebook: user.socialLinks?.facebook || '',
        twitter: user.socialLinks?.twitter || '',
        website: user.socialLinks?.website || ''
      });
      
      if (user.genre && Array.isArray(user.genre)) {
        setGenres(user.genre);
      } else {
        setGenres([]);
      }
    }
    
    // Clear profile image preview
    if (profileImagePreview) {
      URL.revokeObjectURL(profileImagePreview);
      setProfileImagePreview('');
    }
    
    setProfileImage(null);
    setEditMode(false);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.artistName || !formData.artistName.trim()) {
      setLocalError('Artist name is required');
      return;
    }
    
    // Clear any previous errors
    setLocalError('');
    
    try {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadStatus('');
      
      // Prepare profile data for update
      const profileData = {
        username: formData.username,
        artistName: formData.artistName.trim(),
        bio: formData.bio,
        genre: genres,
        socialLinks: {
          instagram: formData.instagram,
          facebook: formData.facebook,
          twitter: formData.twitter,
          website: formData.website
        }
      };
      
      // Progress callback function
      const handleProgress = (progress, status) => {
        setUploadProgress(progress);
        setUploadStatus(status || '');
      };
      
      // Update profile with progress tracking
      await updateProfile(profileData, profileImage, handleProgress);
      
      // Show success message
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
      
      // Exit edit mode
      setEditMode(false);
      
      // Clear profile image preview
      if (profileImagePreview) {
        URL.revokeObjectURL(profileImagePreview);
        setProfileImagePreview('');
      }
      
      setProfileImage(null);
    } catch (err) {
      // Error is already handled in updateProfile
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
    }
  };
  
  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Container maxWidth="lg">
      <Grid container spacing={4}>
        {/* Profile Overview */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Box sx={{ position: 'relative', mb: 3, display: 'inline-block' }}>
              <Avatar
                src={profileImagePreview || getProfileImageUrl(user.profileImage)}
                alt={user.username}
                sx={{ width: 120, height: 120, mx: 'auto' }}
              />
              {editMode && (
                <Box sx={{ position: 'absolute', bottom: 0, right: 0 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="profile-image-upload"
                    type="file"
                    onChange={handleImageChange}
                  />
                  <label htmlFor="profile-image-upload">
                    <IconButton 
                      color="primary" 
                      aria-label="upload picture" 
                      component="span"
                      sx={{ 
                        backgroundColor: 'background.paper',
                        '&:hover': { backgroundColor: 'background.paper' }
                      }}
                    >
                      <CameraIcon />
                    </IconButton>
                  </label>
                </Box>
              )}
            </Box>
            
            <Typography variant="h5" gutterBottom sx={{ color: '#1f1f21' }}>
              {user.artistName || user.username}
            </Typography>
            
            {/* Image upload info */}
            {editMode && profileImage && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Image: {(profileImage.size / (1024 * 1024)).toFixed(1)}MB
                  {profileImage.size > 10 * 1024 * 1024 && (
                    <Chip 
                      label="Direct Upload" 
                      size="small" 
                      color="primary" 
                      sx={{ ml: 1, fontSize: '0.7rem' }}
                    />
                  )}
                </Typography>
              </Box>
            )}
            
            {!editMode ? (
              <>
                {user.bio && (
                  <Typography variant="body2" sx={{ mb: 2, color: '#1f1f21' }}>
                    {user.bio}
                  </Typography>
                )}
                
                {user.genre && user.genre.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: '#1f1f21' }}>
                      Genres
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                      {user.genre.map((g) => (
                        <Chip key={g} label={g} size="small" />
                      ))}
                    </Box>
                  </Box>
                )}
                
                {user.socialLinks && Object.values(user.socialLinks).some(Boolean) && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: '#1f1f21' }}>
                      Social Media
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="center">
                      {user.socialLinks.instagram && (
                        <IconButton 
                          color="primary" 
                          component="a" 
                          href={getSocialDisplayLink(user.socialLinks.instagram, 'instagram')}
                          target="_blank"
                        >
                          <InstagramIcon />
                        </IconButton>
                      )}
                      {user.socialLinks.facebook && (
                        <IconButton 
                          color="primary" 
                          component="a" 
                          href={getSocialDisplayLink(user.socialLinks.facebook, 'facebook')}
                          target="_blank"
                        >
                          <FacebookIcon />
                        </IconButton>
                      )}
                      {user.socialLinks.twitter && (
                        <IconButton 
                          color="primary" 
                          component="a" 
                          href={getSocialDisplayLink(user.socialLinks.twitter, 'twitter')}
                          target="_blank"
                        >
                          <TwitterIcon />
                        </IconButton>
                      )}
                      {user.socialLinks.website && (
                        <IconButton 
                          color="primary" 
                          component="a" 
                          href={getSocialDisplayLink(user.socialLinks.website, 'website')}
                          target="_blank"
                        >
                          <WebIcon />
                        </IconButton>
                      )}
                    </Stack>
                  </Box>
                )}
                
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode(true)}
                  sx={{ mt: 3 }}
                >
                  Edit Profile
                </Button>
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit}
                  disabled={loading || isUploading}
                >
                  {(loading || isUploading) ? <CircularProgress size={24} /> : 'Save'}
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
        
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Profile updated successfully!
            </Alert>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {localError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {localError}
            </Alert>
          )}
          
          {/* Upload Progress */}
          {isUploading && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {uploadStatus || 'Updating profile...'}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                {Math.round(uploadProgress)}%
              </Typography>
            </Box>
          )}
          
          <Paper sx={{ mb: 4 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              aria-label="profile tabs"
            >
              <Tab label="Profile Info" />
              <Tab label="Activity" />
              <Tab label="Settings" />
            </Tabs>
            
            <Box sx={{ p: 3 }}>
              {/* Profile Info Tab */}
              {tabValue === 0 && (
                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Username"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        disabled={!editMode}
                        required
                        sx={{
                          '& .MuiInputLabel-root': { color: '#1f1f21' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#1f1f21' },
                          '& .MuiInputBase-input': { color: '#1f1f21' },
                          '& .MuiInputBase-input.Mui-disabled': { 
                            color: '#666666 !important',
                            WebkitTextFillColor: '#666666 !important'
                          },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#666666' },
                            '&:hover fieldset': { borderColor: '#666666' },
                            '&.Mui-focused fieldset': { borderColor: '#a187f2' },
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        value={formData.email}
                        disabled
                        sx={{
                          '& .MuiInputLabel-root': { color: '#1f1f21' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#1f1f21' },
                          '& .MuiInputBase-input': { color: '#1f1f21' },
                          '& .MuiInputBase-input.Mui-disabled': { 
                            color: '#666666 !important',
                            WebkitTextFillColor: '#666666 !important'
                          },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#666666' },
                            '&:hover fieldset': { borderColor: '#666666' },
                            '&.Mui-focused fieldset': { borderColor: '#a187f2' },
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="Artist/Producer Name"
                        name="artistName"
                        value={formData.artistName}
                        onChange={handleChange}
                        disabled={!editMode}
                        placeholder="How you want to be known"
                        error={!!localError && localError.includes('Artist name')}
                        helperText={localError && localError.includes('Artist name') ? localError : ''}
                        sx={{
                          '& .MuiInputLabel-root': { color: '#1f1f21' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#1f1f21' },
                          '& .MuiInputBase-input': { color: '#1f1f21' },
                          '& .MuiInputBase-input.Mui-disabled': { 
                            color: '#666666 !important',
                            WebkitTextFillColor: '#666666 !important'
                          },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#666666' },
                            '&:hover fieldset': { borderColor: '#666666' },
                            '&.Mui-focused fieldset': { borderColor: '#a187f2' },
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Bio"
                        name="bio"
                        value={formData.bio}
                        onChange={handleChange}
                        disabled={!editMode}
                        multiline
                        rows={4}
                        placeholder="Tell others about yourself and your music"
                        sx={{
                          '& .MuiInputLabel-root': { color: '#1f1f21' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#1f1f21' },
                          '& .MuiInputBase-input': { color: '#1f1f21' },
                          '& .MuiInputBase-input.Mui-disabled': { 
                            color: '#666666 !important',
                            WebkitTextFillColor: '#666666 !important'
                          },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#666666' },
                            '&:hover fieldset': { borderColor: '#666666' },
                            '&.Mui-focused fieldset': { borderColor: '#a187f2' },
                          }
                        }}
                      />
                    </Grid>
                    
                    {editMode && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Add Genre"
                          value={genre}
                          onChange={(e) => setGenre(e.target.value)}
                          onKeyPress={handleGenreKeyPress}
                          sx={{
                            '& .MuiInputLabel-root': { color: '#1f1f21' },
                            '& .MuiInputLabel-root.Mui-focused': { color: '#1f1f21' },
                            '& .MuiInputBase-input': { color: '#1f1f21' },
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: '#666666' },
                              '&:hover fieldset': { borderColor: '#666666' },
                              '&.Mui-focused fieldset': { borderColor: '#a187f2' },
                            }
                          }}
                          InputProps={{
                            endAdornment: (
                              <Button
                                onClick={handleAddGenre}
                                disabled={!genre.trim()}
                                variant="text"
                                sx={{ color: '#1f1f21' }}
                              >
                                Add
                              </Button>
                            ),
                          }}
                        />
                        {genres.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                            {genres.map((g) => (
                              <Chip
                                key={g}
                                label={g}
                                onDelete={() => handleDeleteGenre(g)}
                                size="small"
                              />
                            ))}
                          </Box>
                        )}
                      </Grid>
                    )}
                    
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" gutterBottom sx={{ color: '#1f1f21' }}>
                        Social Links
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Instagram"
                        name="instagram"
                        value={formData.instagram}
                        onChange={handleChange}
                        disabled={!editMode}
                        placeholder="https://instagram.com/yourusername"
                        sx={{
                          '& .MuiInputLabel-root': { color: '#1f1f21' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#1f1f21' },
                          '& .MuiInputBase-input': { color: '#1f1f21' },
                          '& .MuiInputBase-input.Mui-disabled': { 
                            color: '#666666 !important',
                            WebkitTextFillColor: '#666666 !important'
                          },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#666666' },
                            '&:hover fieldset': { borderColor: '#666666' },
                            '&.Mui-focused fieldset': { borderColor: '#a187f2' },
                          }
                        }}
                        InputProps={{
                          startAdornment: <InstagramIcon sx={{ mr: 1, color: '#1f1f21' }} />,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Facebook"
                        name="facebook"
                        value={formData.facebook}
                        onChange={handleChange}
                        disabled={!editMode}
                        placeholder="https://facebook.com/yourpage"
                        sx={{
                          '& .MuiInputLabel-root': { color: '#1f1f21' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#1f1f21' },
                          '& .MuiInputBase-input': { color: '#1f1f21' },
                          '& .MuiInputBase-input.Mui-disabled': { 
                            color: '#666666 !important',
                            WebkitTextFillColor: '#666666 !important'
                          },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#666666' },
                            '&:hover fieldset': { borderColor: '#666666' },
                            '&.Mui-focused fieldset': { borderColor: '#a187f2' },
                          }
                        }}
                        InputProps={{
                          startAdornment: <FacebookIcon sx={{ mr: 1, color: '#1f1f21' }} />,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Twitter"
                        name="twitter"
                        value={formData.twitter}
                        onChange={handleChange}
                        disabled={!editMode}
                        placeholder="https://twitter.com/yourusername"
                        sx={{
                          '& .MuiInputLabel-root': { color: '#1f1f21' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#1f1f21' },
                          '& .MuiInputBase-input': { color: '#1f1f21' },
                          '& .MuiInputBase-input.Mui-disabled': { 
                            color: '#666666 !important',
                            WebkitTextFillColor: '#666666 !important'
                          },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#666666' },
                            '&:hover fieldset': { borderColor: '#666666' },
                            '&.Mui-focused fieldset': { borderColor: '#a187f2' },
                          }
                        }}
                        InputProps={{
                          startAdornment: <TwitterIcon sx={{ mr: 1, color: '#1f1f21' }} />,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Website"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        disabled={!editMode}
                        placeholder="https://yourwebsite.com"
                        sx={{
                          '& .MuiInputLabel-root': { color: '#1f1f21' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#1f1f21' },
                          '& .MuiInputBase-input': { color: '#1f1f21' },
                          '& .MuiInputBase-input.Mui-disabled': { 
                            color: '#666666 !important',
                            WebkitTextFillColor: '#666666 !important'
                          },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#666666' },
                            '&:hover fieldset': { borderColor: '#666666' },
                            '&.Mui-focused fieldset': { borderColor: '#a187f2' },
                          }
                        }}
                        InputProps={{
                          startAdornment: <WebIcon sx={{ mr: 1, color: '#1f1f21' }} />,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {/* Activity Tab */}
              {tabValue === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1f1f21' }}>
                    Recent Activity
                  </Typography>
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ color: '#1f1f21' }}>
                      Activity tracking coming soon!
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, color: '#1f1f21' }}>
                      You'll be able to see your listening history, recent comments, and more.
                    </Typography>
                  </Box>
                </Box>
              )}
              
              {/* Settings Tab */}
              {tabValue === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1f1f21' }}>
                    Account Settings
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: '#1f1f21' }}>
                      Privacy
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="body2" sx={{ color: '#1f1f21' }}>
                        Privacy settings coming soon. You'll be able to control who can see your profile and activity.
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: '#1f1f21' }}>
                      Email Preferences
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="body2" sx={{ color: '#1f1f21' }}>
                        Email notification settings coming soon. You'll be able to choose which notifications you receive.
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: '#1f1f21' }}>
                      Change Password
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      <Button 
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{ mt: 1 }}
                      >
                        Reset Password
                      </Button>
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#1f1f21' }}>
                        You'll receive an email with instructions to reset your password.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProfilePage;